import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import SearchBar from '../common/SearchBar'
import apiService from '../../../../../services/ApiService'
import { useAuth } from '../../../../../context/AuthContext'
import { getProxiedUrl } from '../../../utils/userUtils'
import Profile from '../../../../Profile'

const EMPTY_QUOTA_FORM = {
  monthlyLinkClicksUsed: '',
  monthlyLinkClicksLimit: '',
  monthlyDtaaViewsUsed: '',
  monthlyDtaaViewsLimit: '',
}

const QUOTA_FIELD_CONFIG = [
  { key: 'monthlyLinkClicksUsed', label: 'Link Used' },
  { key: 'monthlyLinkClicksLimit', label: 'Link Limit' },
  { key: 'monthlyDtaaViewsUsed', label: 'DTAA Used' },
  { key: 'monthlyDtaaViewsLimit', label: 'DTAA Limit' },
]

const QUOTA_SECTION_TITLE = 'Monthly Free Tier Quota Controls'

const formatRoleLabel = (roles) => {
  if (Array.isArray(roles)) {
    return roles
      .map((role) => String(role).trim())
      .filter(Boolean)
      .join(', ')
  }

  if (typeof roles === 'string') {
    return roles
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean)
      .join(', ')
  }

  return ''
}

const UserDetailsTab = ({ selectedUser, onUserSelect, showNotification }) => {
  const { isAdmin } = useAuth()
  const canManageQuota = Boolean(isAdmin?.())
  const selectedUserIdentifier = selectedUser?.userId || selectedUser?.email
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [quotaForm, setQuotaForm] = useState(EMPTY_QUOTA_FORM)
  const [quotaLoading, setQuotaLoading] = useState(false)
  const [quotaSaving, setQuotaSaving] = useState(false)
  const [quotaDirty, setQuotaDirty] = useState(false)
  const [quotaError, setQuotaError] = useState('')
  const [quotaSuccess, setQuotaSuccess] = useState('')

  const handleUserSearch = useCallback(async (value) => {
    const trimmedValue = value?.trim()
    setSearchTerm(value)
    setHasSearched(true)
    setSearchError('')

    if (!trimmedValue) {
      setSearchResults([])
      return
    }

    try {
      setSearching(true)
      const response = await apiService.getAdminUsers({
        page: 0,
        size: 10,
        search: trimmedValue,
      })
      setSearchResults(response?.users || [])
    } catch (error) {
      console.error('Failed to search users for details tab:', error)
      setSearchError('Failed to search users. Please try again.')
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const setQuotaFormFromResponse = useCallback((quotaResponse) => {
    setQuotaForm({
      monthlyLinkClicksUsed: String(quotaResponse?.monthlyLinkClicksUsed ?? 0),
      monthlyLinkClicksLimit: String(quotaResponse?.monthlyLinkClicksLimit ?? 0),
      monthlyDtaaViewsUsed: String(quotaResponse?.monthlyDtaaViewsUsed ?? 0),
      monthlyDtaaViewsLimit: String(quotaResponse?.monthlyDtaaViewsLimit ?? 0),
    })
    setQuotaDirty(false)
  }, [])

  const loadUserQuota = useCallback(
    async (userId) => {
      if (!userId) return
      try {
        setQuotaLoading(true)
        setQuotaError('')
        setQuotaSuccess('')
        const quotaResponse = await apiService.getAdminUserQuotaStatus(userId, {
          bypassCache: true,
          skipCache: true,
        })
        setQuotaFormFromResponse(quotaResponse)
      } catch (error) {
        const backendMessage =
          error?.response?.data?.message || error?.message || 'Failed to load user quota'
        setQuotaError(backendMessage)
      } finally {
        setQuotaLoading(false)
      }
    },
    [setQuotaFormFromResponse]
  )

  useEffect(() => {
    if (!canManageQuota || !selectedUserIdentifier) {
      setQuotaForm(EMPTY_QUOTA_FORM)
      setQuotaDirty(false)
      setQuotaError('')
      setQuotaSuccess('')
      return
    }
    loadUserQuota(selectedUserIdentifier)
  }, [canManageQuota, loadUserQuota, selectedUserIdentifier])

  const handleQuotaInputChange = (fieldKey) => (event) => {
    const nextValue = event.target.value?.trim()
    if (nextValue !== '' && !/^\d+$/.test(nextValue)) {
      return
    }
    setQuotaForm((prev) => ({
      ...prev,
      [fieldKey]: nextValue,
    }))
    setQuotaDirty(true)
    setQuotaError('')
    setQuotaSuccess('')
  }

  const buildQuotaPayload = () => {
    const parseInteger = (key, label) => {
      const rawValue = quotaForm[key]
      if (!/^\d+$/.test(rawValue)) {
        throw new Error(`${label} must be a non-negative integer`)
      }
      const parsed = Number.parseInt(rawValue, 10)
      if (!Number.isInteger(parsed) || parsed < 0) {
        throw new Error(`${label} must be a non-negative integer`)
      }
      return parsed
    }

    const monthlyLinkClicksUsed = parseInteger('monthlyLinkClicksUsed', 'Link Used')
    const monthlyLinkClicksLimit = parseInteger('monthlyLinkClicksLimit', 'Link Limit')
    const monthlyDtaaViewsUsed = parseInteger('monthlyDtaaViewsUsed', 'DTAA Used')
    const monthlyDtaaViewsLimit = parseInteger('monthlyDtaaViewsLimit', 'DTAA Limit')

    if (monthlyLinkClicksUsed > monthlyLinkClicksLimit) {
      throw new Error('Link Used cannot be greater than Link Limit')
    }
    if (monthlyDtaaViewsUsed > monthlyDtaaViewsLimit) {
      throw new Error('DTAA Used cannot be greater than DTAA Limit')
    }

    return {
      monthlyLinkClicksUsed,
      monthlyLinkClicksLimit,
      monthlyDtaaViewsUsed,
      monthlyDtaaViewsLimit,
    }
  }

  const handleQuotaSave = async () => {
    if (!canManageQuota || !selectedUserIdentifier) return
    try {
      const payload = buildQuotaPayload()
      setQuotaSaving(true)
      setQuotaError('')
      const updatedQuota = await apiService.updateAdminUserQuota(
        selectedUserIdentifier,
        payload
      )
      setQuotaFormFromResponse(updatedQuota || payload)
      setQuotaSuccess('Quota updated')
      if (typeof showNotification === 'function') {
        showNotification('User quota updated successfully', 'success')
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to update user quota'
      setQuotaError(message)
      if (typeof showNotification === 'function') {
        showNotification(message, 'error')
      }
    } finally {
      setQuotaSaving(false)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          User Details
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleUserSearch}
            placeholder='Search user by name or email...'
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
          <Button
            variant='contained'
            size='small'
            onClick={() => handleUserSearch(searchTerm)}
            disabled={searching}
            sx={{
              bgcolor: '#232536',
              '&:hover': { bgcolor: '#1a1d28' },
              width: { xs: '100%', sm: 'auto' },
              px: 2,
              py: 0.9,
            }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {searchError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {searchError}
        </Alert>
      )}

      {searching && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant='body2' sx={{ ml: 1.5 }}>
            Searching users...
          </Typography>
        </Box>
      )}

      {!searching && hasSearched && searchResults.length > 0 && (
        <Paper
          variant='outlined'
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <List disablePadding>
            {searchResults.map((user) => {
              const roleLabel = formatRoleLabel(user.roles)

              return (
                <ListItemButton
                  key={user.userId || user.email}
                  selected={selectedUser?.userId === user.userId}
                  onClick={() => onUserSelect?.(user)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1.25,
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: { xs: 48, sm: 56 }, mt: 0.25 }}>
                    <Avatar
                      src={getProxiedUrl(user.profilePictureUrl)}
                      alt={user.name}
                      imgProps={{
                        onError: (event) => {
                          event.target.src = ''
                        },
                        referrerPolicy: 'no-referrer',
                      }}
                    >
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: { xs: 0.75, sm: 1.5 },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant='subtitle1'
                        sx={{
                          fontWeight: 600,
                          lineHeight: 1.25,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {user.name || 'Unnamed User'}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                          mt: 0.25,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          lineHeight: 1.35,
                        }}
                      >
                        {user.email}
                      </Typography>
                    </Box>

                    {roleLabel && (
                      <Chip
                        size='small'
                        label={roleLabel}
                        variant='outlined'
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          maxWidth: { xs: '100%', sm: 220 },
                          height: 'auto',
                          '& .MuiChip-label': {
                            display: 'block',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            py: 0.25,
                          },
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              )
            })}
          </List>
        </Paper>
      )}

      {!searching && hasSearched && searchTerm?.trim() && searchResults.length === 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>
          No users found for the current search.
        </Alert>
      )}

      {canManageQuota && selectedUser && (
        <Paper
          variant='outlined'
          sx={{
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            borderColor: '#e5e7eb',
            backgroundColor: '#fafbfc',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1.25, sm: 1.5 },
              mb: 1.5,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box>
              <Typography
                variant='subtitle1'
                sx={{ fontWeight: 700, lineHeight: 1.25 }}
              >
                {QUOTA_SECTION_TITLE}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 0.25 }}
              >
                Edit this user's monthly usage and limits.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(2, auto)' },
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                size='small'
                variant='outlined'
                onClick={() => loadUserQuota(selectedUserIdentifier)}
                disabled={quotaLoading || quotaSaving}
                sx={{
                  textTransform: 'none',
                  minHeight: 36,
                }}
              >
                Reset
              </Button>
              <Button
                size='small'
                variant='contained'
                onClick={handleQuotaSave}
                disabled={quotaLoading || quotaSaving || !quotaDirty}
                sx={{
                  bgcolor: '#232536',
                  '&:hover': { bgcolor: '#1a1d28' },
                  textTransform: 'none',
                  minHeight: 36,
                }}
              >
                Save
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 1.25, sm: 1.5 },
            }}
          >
            {QUOTA_FIELD_CONFIG.map((field) => (
              <TextField
                key={field.key}
                size='small'
                label={field.label}
                value={quotaForm[field.key]}
                onChange={handleQuotaInputChange(field.key)}
                disabled={quotaLoading || quotaSaving}
                fullWidth
                sx={{
                  '& .MuiInputBase-input': {
                    fontWeight: 600,
                  },
                }}
                inputProps={{
                  min: 0,
                  step: 1,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            ))}
          </Box>

          {quotaError && (
            <Alert severity='error' sx={{ mt: 1.5 }}>
              {quotaError}
            </Alert>
          )}
          {quotaSuccess && !quotaError && (
            <Alert severity='success' sx={{ mt: 1.5 }}>
              {quotaSuccess}
            </Alert>
          )}
        </Paper>
      )}

      {selectedUser ? (
        <Profile
          key={selectedUser.userId || selectedUser.email}
          adminUserId={selectedUser.userId}
          adminUserSummary={selectedUser}
          embedded={true}
          syncTabWithQuery={false}
        />
      ) : (
        <Alert severity='info'>
          Search and select a user to view complete profile details.
        </Alert>
      )}
    </Box>
  )
}

export default UserDetailsTab
